import type { EClickRankType, IClickmapSorted } from '@gem-sdk/heatmap-react';
import type { IClickRankMetricKey } from './filter-config';

import {
  isHiddenBehindOther,
  useHeatmapDataContext,
  useHeatmapSettingContext,
  useHeatmapVizContext,
  useHeatmapVizRectContext,
} from '@gem-sdk/heatmap-react';
import { HEATMAP_CLICK_CONFIG } from '../configs/heatmap-click';
import { useHeatmapClickRankConfig } from './filter-config';
import { useCallback, useEffect } from 'react';
import { sortByConditionV2 } from '~/utils';

interface IMapDataWithMetric {
  hash: string;
  selector: string;
  listSelectors: string[];
  value: number;
}
export const useHeatmapClickRank = () => {
  const vizRef = useHeatmapVizRectContext((s) => s.vizRef);
  const dataInfo = useHeatmapDataContext((s) => s.dataInfo);
  const clickmap = useHeatmapDataContext((s) => s.clickmap);
  const rankedBy = useHeatmapSettingContext((s) => s.rankedBy);
  const isDomLoaded = useHeatmapVizContext((s) => s.isDomLoaded);
  const setRankedBy = useHeatmapSettingContext((s) => s.setRankedBy);
  const setDataInfoByKey = useHeatmapDataContext((s) => s.setDataInfoByKey);

  const { configMap } = useHeatmapClickRankConfig();

  const clickMapMetrics = dataInfo?.clickMapMetrics;

  const handleSortClickmap = useCallback(
    (rankedBy: EClickRankType) => {
      if (!clickmap || !vizRef || !isDomLoaded) return;

      const metricKey = configMap[rankedBy].metricKey;
      const dataWithMetrics: IMapDataWithMetric[] = [];

      for (const item of clickmap) {
        const hash = item.hash;

        const elm = vizRef.get(hash);
        if (!elm) continue;

        const isHidden = isHiddenBehindOther(elm);
        if (isHidden) continue;

        const elmData = clickMapMetrics?.[hash];
        const selector = elmData?.selector ?? '';
        const listSelectors = elmData?.listSelectors ?? [];

        const metric: Record<IClickRankMetricKey, number> = {
          totalClicked: elmData?.totalClicked?.value ?? 0,
          totalOrders: elmData?.totalOrders?.value ?? 0,
          totalRevenue: elmData?.totalRevenue?.value ?? 0,
          totalSessions: elmData?.totalSessions?.value ?? 0,
          conversionRate: elmData?.conversionRate?.value ?? 0,
          revenuePerClick: elmData?.revenuePerClick?.value ?? 0,
          revenuePerSession: elmData?.revenuePerSession?.value ?? 0,
        };

        const metricValue = Number(metric?.[metricKey as IClickRankMetricKey]) ?? 0;
        dataWithMetrics.push({ hash, selector, listSelectors, value: metricValue });
      }

      const sorted = sortByConditionV2(dataWithMetrics, { attr: 'value', order: 'desc' });
      const sortedData: IClickmapSorted[] = sorted.map(
        ({ hash, selector, listSelectors }, index) => ({
          hash,
          selector,
          listSelectors,
          rank: index + 1,
        }),
      );

      setDataInfoByKey('sortedElements', sortedData);
    },
    [clickmap, vizRef, isDomLoaded, configMap, setDataInfoByKey, clickMapMetrics],
  );

  useEffect(() => {
    if (!rankedBy) return;

    handleSortClickmap(rankedBy);
  }, [rankedBy, handleSortClickmap]);

  useEffect(() => {
    if (rankedBy) return;

    setRankedBy(HEATMAP_CLICK_CONFIG.RANKED_BY);
  }, [rankedBy, setRankedBy]);

  return;
};
